import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { LocalStorageService } from 'src/app/local-storage.service';
import { ServiceProviderIndividualServicePlanActivityDialogComponent } from '../service-provider-individual-service-plan-activity-dialog/service-provider-individual-service-plan-activity-dialog.component';
import { ServiceProviderSubjectService } from 'src/services/service-provider-subject.service';
import { ToastrImplService } from 'src/services/toastr.service';
import { ServiceProvidersService } from 'src/services/service-providers.service';

@Component({
  selector: 'app-service-provider-individual-service-plan-dialog',
  templateUrl: './service-provider-individual-service-plan-dialog.component.html',
  styleUrls: ['./service-provider-individual-service-plan-dialog.component.scss']
})
export class ServiceProviderIndividualServicePlanDialogComponent implements OnInit {
  isLoading = false;
  firstStep: FormGroup;
  secondStep: FormGroup;
  isEditMode = false;
  maxDate: Date = new Date(2999, 11, 31);

  @Input() formNumber: any;
  @Input() approved: boolean;
  @Output() handleSubmitData = new EventEmitter();
  @Output() handleSaveChanges = new EventEmitter();
  @Output() handleFormNumberChange = new EventEmitter<number>();
  @ViewChild(MatTable, { static: true }) table: MatTable<any>;
  @ViewChild('submitButton1') submitButton1;
  @ViewChild('submitButton2') submitButton2;

  subject: any;
  professionalWorkerName: any;
  showDate: any;
  currentDate = new Date();
  individualServicePlanForm: any = {};

  activities: FormArray;
  @ViewChild('activityTable') activityTable: MatTable<any>;
  activityDataSource: any[] = [];
  displayedActivityColumns = ['activityDescription', 'activityGoal', 'expectedOutcome', 'realizationPeriodFrom', 'realizationPeriodTo', 'reviewDeadline', 'actions'];
  signatories: any = [];

  constructor(
    private formBuilder: FormBuilder,
    private localStorageService: LocalStorageService,
    private dialogRef: MatDialogRef<ServiceProviderIndividualServicePlanDialogComponent>,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private serviceProviderSubjectService: ServiceProviderSubjectService,
    private toastr: ToastrImplService,
    private serviceProvidersService: ServiceProvidersService
  ) {
    this.showDate = this.datePipe.transform(this.currentDate, 'yyyy-MM-dd');
    this.professionalWorkerName = (JSON.parse(this.localStorageService.get('loggedUser'))).firstName + ' ' + (JSON.parse(this.localStorageService.get('loggedUser'))).lastName;
    this.subject = data?.subject;

    this.serviceProvidersService.getSignatoriesUsersInServiceProvider(this.subject.serviceProviderId).subscribe(result => {
      this.signatories = result;
    });
  }

  ngOnInit(): void {
    this.createForms();
  }

  loadData(data: any) {
    if (!data) {
      this.individualServicePlanForm = {};
  
      this.activities.clear();
      this.activityDataSource = [];
  
      return;
    }
    
    this.individualServicePlanForm = data;
    this.firstStep.patchValue({
      signatory: data.signatory
    });
    this.activities.clear();

    if (this.individualServicePlanForm.activities && this.individualServicePlanForm.activities.length > 0) {
      this.individualServicePlanForm.activities.forEach(ac => {
        this.activities.push(
          this.formBuilder.group({
            id: [ac.id],
            activityDescription: [ac.activityDescription],
            activityGoal: [ac.activityGoal],
            expectedOutcome: [ac.expectedOutcome],
            realizationPeriodFrom: [ac.realizationPeriodFrom],
            realizationPeriodTo: [ac.realizationPeriodTo],
            reviewDeadline: [ac.reviewDeadline]
          })
        );
      });
    }

    this.activityDataSource = [...this.activities.controls];
    if (this.activityTable) {
      this.activityTable.renderRows();
    }
  }

  createForms() {
    this.activities = this.formBuilder.array([]);
    this.firstStep = this.formBuilder.group({
      dateOfSubmission: [this.showDate, [Validators.required]],
      firstName: [this.subject.firstName, []],
      lastName: [this.subject.lastName, []],
      parentName: [this.subject.parentName, []],
      gender: [this.subject.gender, []],
      dateOfBirth: [this.subject.dateOfBirth, []],
      jmbg: [this.subject.jmbg, []],
      permanentResidence: [this.subject.permanentResidence, []],
      phone: [this.subject.phone, []],
      residence: [this.subject.residence, []],
      signatory: [null, [Validators.required]]
    });
  
    this.secondStep = this.formBuilder.group({
      activities: this.activities,
    });

    this.serviceProviderSubjectService.getIndividualServicePlanForm(this.subject.id).subscribe(result => this.loadData(result));
  }

  addActivity() {
    const dialogRef = this.dialog.open(ServiceProviderIndividualServicePlanActivityDialogComponent, {
      width: '600px',
      disableClose: true,
      data: null,
      panelClass: 'overlay-panel',
    });
  
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.activities.push(
          this.formBuilder.group({
            id: [null],            
            activityDescription: [result.activityDescription],
            activityGoal: [result.activityGoal],
            expectedOutcome: [result.expectedOutcome],
            realizationPeriodFrom: [result.realizationPeriodFrom],
            realizationPeriodTo: [result.realizationPeriodTo],
            reviewDeadline: [result.reviewDeadline]
          })
        );
        this.activityDataSource = [...this.activities.controls];
        if (this.activityTable) {
          this.activityTable.renderRows();
        }
      }
    });
  }
    
  editActivity(index: number) {
    const ac = this.activities.at(index).value;
  
    const dialogRef = this.dialog.open(ServiceProviderIndividualServicePlanActivityDialogComponent, {
      width: '600px',
      disableClose: true,
      data: ac,
      panelClass: 'overlay-panel',
    });
  
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const existingId = this.activities.at(index).value.id;
        this.activities.at(index).patchValue({
          id: existingId,
          activityDescription: result.activityDescription,
          activityGoal: result.activityGoal,
          expectedOutcome: result.expectedOutcome,
          realizationPeriodFrom: result.realizationPeriodFrom,
          realizationPeriodTo: result.realizationPeriodTo,
          reviewDeadline: result.reviewDeadline
        });
        this.activityDataSource = [...this.activities.controls];
        this.activityTable.renderRows();
      }
    });
  }
    
  removeActivity(index: number) {
    this.activities.removeAt(index);
    this.activityDataSource = [...this.activities.controls];
    this.activityTable.renderRows();
  }

  preview(){        
    const submittedData = this.createObject(false);
    this.serviceProviderSubjectService.previewIndividualServicePlanForm(submittedData);
  }

  createObject(isFinished: boolean){
    const submittedData = {
      id: (this.individualServicePlanForm?.id !== null && this.individualServicePlanForm?.id !== undefined) ? this.individualServicePlanForm?.id : null,
      dateOfSubmission: this.firstStep.value.dateOfSubmission,
      serviceProviderId: this.subject.serviceProviderId,
      serviceProviderSubjectId: this.subject.id,
      activities: this.activities.value.map(ac => ({
        id: ac.id ?? null,
        activityDescription: ac.activityDescription,
        activityGoal: ac.activityGoal,
        expectedOutcome: ac.expectedOutcome,
        realizationPeriodFrom: ac.realizationPeriodFrom,
        realizationPeriodTo: ac.realizationPeriodTo,
        reviewDeadline: ac.reviewDeadline
      })),
      professionalWorkerName: this.professionalWorkerName,
      signatory: this.firstStep.value.signatory,
      finished: isFinished,
    };
    return submittedData;
  }

  save(isFinished: any) {
    const submittedData = this.createObject(isFinished);
    this.serviceProviderSubjectService.submitIndividualServicePlanForm(submittedData).subscribe(
      (result) => {
        if (isFinished) {
          this.toastr.success('SERVICE_PROVIDERS.INDIVIDUAL_SERVICE_PLAN.GENERATE_SUCCESS');
          this.handleSaveChanges.emit({ step: 'STEP_02', isFinished: true, saved: true });
        } else {
          this.toastr.success('VS.INITIAL_ASSESSMENT.FORMS23.SAVED');
          this.handleSaveChanges.emit({ step: 'STEP_02', isFinished: false, saved: true });
        }
        this.dialogRef.close(result);
      }, () => {
        this.submitButton1.disabled = false;
        this.submitButton2.disabled = false;
        this.toastr.error('SNACKBAR.ERROR_OCCURRED');
      }
    );
  }

  handleSubmit() {
    this.submitButton1.disabled = true;
    this.submitButton2.disabled = true;
    this.save(true);
  }

  closeForm() {
    this.isEditMode = false;
    this.dialogRef.close();
  }

  compareObjects(o1: any, o2: any): boolean{
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
  }
  
}
